import { useContext } from 'react';
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { Link, useParams } from 'react-router';
import { TypographySmallTitle } from './DocumentDetailsLeft';
import DrawIcon from '@mui/icons-material/Draw';
import { IDocumentUnscoped, ITrainingSet } from '../../../types/my-types';
import CollapsedTitleWithTooltip from '../../common/CollapsedTitleWithTooltip';
import { DocumentDetailsParamTypes } from '../../../pages/DocumentDetailsPage';
import AddToTrainingSet from './AddToTrainingSet';
import CreateTrainingSet from './CreateTrainingSet';
import { useQuery } from '@tanstack/react-query';
import useGlynt from '../../hooks/useGlynt';
import TrainingSetDeleteDocument from '../../train/actions/TrainingSetDeleteDocument';
import { AppContext } from '../../../App';
import { compactTableStyles } from '../../common/tableStyles';
import { LinkTableStyled } from '../../common/StyledComponents';

interface IAssignedTrainingSetListProps {
  documentData: IDocumentUnscoped;
}

const AssignedTrainingSetList = ({ documentData }: IAssignedTrainingSetListProps) => {
  const { dataPoolId } = useContext(AppContext);
  const glynt = useGlynt();
  const { dpid, documentId } = useParams<DocumentDetailsParamTypes>();
  const theme = useTheme();

  if (!documentId) {
    return null;
  }

  const { data: trainingSets, isFetching: isFetchingTrainingSets } = useQuery({
    queryKey: ['trainingSetsByDocument'],

    queryFn: async () =>
      (await glynt).trainingSet.list({
        advanced: true,
        ordering: 'label',
        limit: 100,
        document: documentId,
      }),

    refetchOnWindowFocus: false
  });

  return (
    <div style={{ marginTop: '20px' }}>
      <TypographySmallTitle>SAMPLE IN TRAINING SETS</TypographySmallTitle>

      <Box sx={{ ...compactTableStyles.containerSx, mt: 1 }}>
        <Table sx={{ ...compactTableStyles.tableSx, minWidth: 0 }}>
          <TableHead sx={compactTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell colSpan={2}>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={compactTableStyles.tableBodySx}>
            {isFetchingTrainingSets && (
              <TableRow>
                <TableCell colSpan={2}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {!isFetchingTrainingSets && trainingSets?.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>No training sets found.</TableCell>
              </TableRow>
            )}
            {!isFetchingTrainingSets &&
              trainingSets?.results.map((trainingSet: ITrainingSet, index: number) => (
                <TableRow key={index}>
                  <TableCell>
                    <LinkTableStyled
                      to={`/data-pools/${dpid}/train/${trainingSet.id}/details`}
                      data-cy={`link-training-set-${trainingSet.label}`}
                    >
                      <CollapsedTitleWithTooltip title={trainingSet.label} charsFirstAndLast={15} />
                    </LinkTableStyled>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      placement="top"
                      title={`Mark`}
                      disableFocusListener
                      disableTouchListener
                    >
                      <Link
                        to={`/data-pools/${dataPoolId}/train/${trainingSet.id}/mark/${documentData.id}`}
                        style={{ textDecoration: 'none', color: theme.palette.custom.lightGreen }}
                        data-cy={`icon-mark-document-${trainingSet.label}`}
                      >
                        <DrawIcon fontSize="small" />
                      </Link>
                    </Tooltip>

                    <TrainingSetDeleteDocument
                      trainingSet={trainingSet}
                      documentLabel={documentData.label}
                      documentId={documentData.id}
                      tooltip="Remove Document"
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Box>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', gap: '10px' }}>
        <AddToTrainingSet documentId={documentId} />
        <CreateTrainingSet documentId={documentId} />
      </div>
    </div>
  );
};

export default AssignedTrainingSetList;
